"use client";

import { useState, useRef, useEffect } from "react";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import PublicLayout from "@/components/PublicLayout";
import { ShieldCheck, Mail, ArrowUp } from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";
import { usePageContent } from "@/hooks/usePageContent";
import { useMetadata } from "@/hooks/useMetadata";

type TermsSection = {
  id: string;
  title: string;
  content: React.ReactNode;
};

export const TERMS_COPY: TermsSection[] = [
  {
    id: "acceptance-of-terms",
    title: "1. Acceptance of Terms",
    content: (
      <p className="leading-relaxed">
        By accessing or using UniUI, you agree to be bound by these Terms of
        Service. If you do not agree with any part of these terms, you may not
        access or use the platform.
      </p>
    ),
  },
  {
    id: "eligibility",
    title: "2. Eligibility",
    content: (
      <p className="leading-relaxed">
        You must be at least 16 years old and a currently enrolled student at a
        recognised Nigerian tertiary institution to use UniUI. By creating an
        account, you represent and warrant that you meet these requirements.
        Accounts suspected of violating eligibility criteria may be suspended
        pending verification.
      </p>
    ),
  },
  {
    id: "account-responsibilities",
    title: "3. Account Responsibilities",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>You are responsible for:</p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>Maintaining the confidentiality of your account credentials.</li>
          <li>All activities that occur under your account.</li>
          <li>
            Notifying us immediately of unknown unauthorised access or security
            breach.
          </li>
          <li>
            Ensuring that your account information remains accurate and up to
            date.
          </li>
        </ul>
        <p>
          UniUI shall not be liable for unknown loss or damage arising from your
          failure to maintain the security of your account.
        </p>
      </div>
    ),
  },
  {
    id: "acceptable-use",
    title: "4. Acceptable Use",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>You agree not to:</p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            Upload malicious files, scripts, or code designed to harm the
            platform or its users.
          </li>
          <li>
            Attempt to breach, test, or circumvent the platform&apos;s security
            measures.
          </li>
          <li>
            Use the platform for any illegal, fraudulent, or unauthorised
            purpose.
          </li>
          <li>
            Harass, abuse, or harm other users through any interaction medium.
          </li>
          <li>
            Upload copyrighted material that you do not have the legal right to
            distribute.
          </li>
          <li>
            Attempt to manipulate the AI to produce harmful, inappropriate, or
            academically dishonest content.
          </li>
        </ul>
        <p>
          Violation of these provisions may result in immediate account
          suspension, data deletion, or referral to appropriate academic
          authorities.
        </p>
      </div>
    ),
  },
  {
    id: "user-generated-content",
    title: "5. User-Generated Content",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          You retain ownership of any materials you upload to UniUI (lecture
          notes, past questions, slides, etc.). By uploading content, you grant
          UniUI a non‑exclusive, worldwide, royalty‑free licence to use,
          reproduce, modify, and distribute the content for the purpose of
          providing and improving the platform, including AI model training.
        </p>
        <p>
          You represent and warrant that you have the right to share any content
          you upload. Uploaded materials are visible to other students enrolled
          in the same course to support collaborative academic inquiry.
        </p>
      </div>
    ),
  },
  {
    id: "content-upload-policy",
    title: "6. Content Upload & Copyright Policy",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          UniUI provides an Upload feature so you can feed your own lecture
          notes, past questions, slides, images and audio into your assistant.
          To protect the academic community from copyright infringement, you
          must only upload content you own or have explicit permission to share.
        </p>
        <p>
          Uploaded materials are automatically screened by content moderation.
          Any material determined to be copyrighted, abusive, or otherwise in
          breach of these Terms may be removed without notice, and the
          responsible account may be restricted or terminated. Repeat violations
          will result in permanent account termination and forfeiture of any
          remaining credits or subscription.
        </p>
        <p>
          UniUI respects intellectual property rights and will respond to
          takedown notices in accordance with applicable law. If you believe
          material on the platform infringes your copyright, contact us at{" "}
          <a
            href="mailto:support@uniui.com.ng"
            className="text-y font-bold hover:underline"
          >
            support@uniui.com.ng
          </a>
          .
        </p>
      </div>
    ),
  },
  {
    id: "ai-generated-content",
    title: "7. AI-Generated Content",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          UniUI uses artificial intelligence to generate academic answers and
          study resources. While we strive for accuracy, AI‑generated content
          may contain errors, omissions, or outdated information.
        </p>
        <p>
          UniUI is an academic aid — not a substitute for attending lectures,
          studying official course materials, or consulting your lecturers.
          Always verify critical information with official university sources
          before relying on it for examinations or assignments.
        </p>
      </div>
    ),
  },
  {
    id: "intellectual-property",
    title: "8. Intellectual Property",
    content: (
      <p className="leading-relaxed">
        The UniUI platform, including its source code, design, logo, proprietary
        AI models, and Knowledge Graph architecture, is the exclusive
        intellectual property of UniUI. You may not copy, modify, distribute, or
        reverse-engineer any part of the platform without explicit written
        permission from UniUI.
      </p>
    ),
  },
  {
    id: "payments-and-credits",
    title: "9. Payments & Credits",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          All payments are processed securely through Paystack. Key provisions
          include:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            <strong>Non-refundability</strong> — credits are non‑refundable
            except where required by applicable consumer protection law.
          </li>
          <li>
            <strong>Expiry</strong> — unused credits expire 12 months after the
            date of purchase.
          </li>
          <li>
            <strong>Exam Passes</strong> — subscription-based Exam Passes are
            valid for the specified duration and are non‑transferable between
            accounts.
          </li>
          <li>
            <strong>Promotional pricing</strong> — discounts applied via promo
            codes reduce the amount charged through Paystack. Fraudulent use of
            promo codes may result in account termination.
          </li>
        </ul>
      </div>
    ),
  },
  {
    id: "content-protection",
    title: "10. Content Protection and Prohibited Actions",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          All materials provided by UniUI, including but not limited to answers,
          notes, formula sheets, worked examples, diagrams, audio summaries, and
          any other output generated by the platform (collectively,
          &ldquo;Content&rdquo;), are the exclusive intellectual property of
          UniUI or its licensors.
        </p>
        <p>You agree NOT to:</p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            Take screenshots, screen recordings, photographs, or any form of
            capture of the Content, except where explicitly permitted by the
            platform&rsquo;s &ldquo;Share&rdquo; functionality.
          </li>
          <li>
            Reproduce, duplicate, copy, sell, resell, or exploit any portion of
            the Content without express written permission.
          </li>
          <li>
            Create derivative works, compilations, or collections that include
            Content from UniUI.
          </li>
          <li>
            Use any automated means (scraping, bots, etc.) to access or collect
            Content.
          </li>
          <li>
            Share, distribute, or make available any captured Content on social
            media, messaging apps, websites, or any other medium.
          </li>
        </ul>
        <p>
          To protect Content, UniUI employs digital watermarking and other
          technical measures. Any screenshot or reproduction may contain
          identifiable information linking it to your account. UniUI reserves
          the right to monitor and log screenshot attempts.
        </p>
        <p>
          Violation of this clause will result in immediate termination of your
          account and forfeiture of any remaining credits or subscription. UniUI
          may seek legal remedies, including injunctive relief and damages, for
          any breach that causes harm to the platform&rsquo;s intellectual
          property or business interests.
        </p>
        <p>
          If you wish to share Content for educational purposes, you must use
          the official &ldquo;Share Preview Link&rdquo; or &ldquo;Share as
          Image&rdquo; features provided within the application. Any other
          sharing method is strictly prohibited.
        </p>
      </div>
    ),
  },
  {
    id: "limitation-of-liability",
    title: "11. Limitation of Liability",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          UniUI is provided &quot;as is&quot; and &quot;as available&quot;
          without warranties of any kind, either express or implied. We do not
          warrant that the platform will be uninterrupted, secure, or
          error-free.
        </p>
        <p>
          To the fullest extent permitted by law, UniUI shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages,
          including but not limited to academic consequences arising from
          reliance on AI‑generated answers.
        </p>
      </div>
    ),
  },
  {
    id: "termination",
    title: "12. Termination",
    content: (
      <div className="space-y-4 leading-relaxed">
        <p>
          We reserve the right to suspend or terminate your account at our sole
          discretion if you violate these Terms of Service, engage in abusive
          behaviour, or pose a security risk to the platform or its community.
        </p>
        <p>
          You may delete your account at any time via{" "}
          <strong>Settings → Account → Delete Account</strong>. Account deletion
          initiates a 30-day data purge window during which all associated data
          is permanently removed.
        </p>
      </div>
    ),
  },
  {
    id: "changes-to-terms",
    title: "13. Changes to Terms",
    content: (
      <p className="leading-relaxed">
        We may update these Terms of Service from time to time to reflect
        changes in our practices, features, or legal obligations. Material
        changes will be communicated via email or in-platform notification at
        least 14 days before they take effect. Continued use of the platform
        after changes constitute acceptance of the revised terms.
      </p>
    ),
  },
  {
    id: "governing-law",
    title: "14. Governing Law",
    content: (
      <p className="leading-relaxed">
        These Terms of Service are governed by and construed in accordance with
        the laws of the Federal Republic of Nigeria. Any disputes arising from
        these terms or your use of the platform shall be resolved in the courts
        of Nigeria.
      </p>
    ),
  },
  {
    id: "contact",
    title: "15. Contact",
    content: (
      <p className="leading-relaxed">
        For questions, concerns, or legal notices regarding these Terms of
        Service, please contact us at{" "}
        <a
          href="mailto:support@uniui.com.ng"
          className="text-y font-bold hover:underline"
        >
          support@uniui.com.ng
        </a>{" "}
        or visit our{" "}
        <Link href="/contact" className="text-y font-bold hover:underline">
          Contact page
        </Link>
        .
      </p>
    ),
  },
];

export default function TermsOfServicePage() {
  useMetadata({ key: "terms" });
  const [activeSection, setActiveSection] = useState<string>("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const copy = usePageContent("terms", TERMS_COPY);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        rootMargin: "-10% 0px -80% 0px",
      },
    );

    copy.forEach((section) => {
      const ref = sectionRefs.current[section.id];
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setSectionRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  return (
    <PageTransition>
      <PublicLayout>
        <div className="min-h-screen bg-background">
          <div className="max-w-6xl mx-auto px-6 py-12">
            <motion.header
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12 space-y-3"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <ShieldCheck className="w-8 h-8 text-y" />
                <h1 className="display text-4xl md:text-5xl tracking-tight">
                  Terms of Service
                </h1>
              </div>
              <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">
                Last updated: September 2026 (v0.14.0)
              </p>
            </motion.header>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-8 space-y-10">
                {copy.map((section, index) => (
                  <motion.section
                    key={section.id}
                    id={section.id}
                    ref={setSectionRef(section.id)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.04 }}
                    className="scroll-mt-24"
                  >
                    <div className="matte-metal p-8 md:p-10 rounded-[2rem] space-y-5">
                      <h2 className="display text-xl md:text-2xl tracking-tight text-foreground">
                        {section.title}
                      </h2>
                      <div className="text-sm text-muted-foreground leading-relaxed font-medium space-y-4">
                        {section.content}
                      </div>
                    </div>
                  </motion.section>
                ))}
              </div>

              <aside className="hidden lg:block lg:col-span-4">
                <div className="sticky top-24 space-y-4">
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground mb-2">
                    Table of Contents
                  </div>
                  <nav className="space-y-1 border-l border-border pl-1">
                    {copy.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => {
                          const ref = sectionRefs.current[section.id];
                          if (ref)
                            ref.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }}
                        className={`block w-full text-left py-1.5 pl-3 rounded-lg text-xs font-black uppercase tracking-widest transition-all duration-200 ${
                          activeSection === section.id
                            ? "toc-item-active bg-y/5"
                            : "text-muted-foreground hover:text-foreground hover:translate-x-1"
                        }`}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>
            </div>
          </div>

          {showBackToTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={scrollToTop}
              className="back-to-top-fab"
              aria-label="Back to top"
            >
              <ArrowUp className="w-5 h-5" strokeWidth={3} />
            </motion.button>
          )}
        </div>
      </PublicLayout>
    </PageTransition>
  );
}
